/**
 * WhatsAppSettings
 *
 * Três painéis:
 *  1. Conexão — status do número YCloud, dados do provider
 *  2. Limites do plano — caps definidos pelo superadmin (read-only)
 *  3. Configurações de envio — editáveis pelo tenant-admin
 */

import React, { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  CheckCircle2, XCircle, AlertCircle, Loader2, Wifi,
  ShieldAlert, Clock, MessageSquare, Users,
} from 'lucide-react';
import { supabase } from '@/lib/supabase';

// ── Tipos ──────────────────────────────────────────────────────────────────────

interface TenantWhatsApp {
  provider:        string;
  display_phone:   string;
  status:          'pending' | 'active' | 'suspended' | 'inactive';
  tier:            number;
  daily_limit:     number;
  connected_at:    string | null;
  last_health_check: string | null;
  health_status:   string | null;
}

interface TenantPlanLimits {
  plan_name:              string;
  daily_message_limit:    number;
  monthly_message_limit:  number;
  max_campaign_size:      number;
  max_campaigns_per_day:  number;
  allowed_categories:     string[];
  min_cooldown_minutes:   number;
  reputation_floor:       number;
}

interface TenantMessagingSettings {
  daily_message_target:              number | null;
  max_msgs_per_contact_per_week:     number;
  max_msgs_per_contact_per_month:    number;
  require_confirmation_above:        number;
  optout_footer:                     string;
  send_window_start:                 string;
  send_window_end:                   string;
  allowed_weekdays:                  number[];
  emergency_pause:                   boolean;
  pause_reason:                      string | null;
}

// ── Zod schema para o formulário de settings ───────────────────────────────────

const settingsSchema = z.object({
  max_msgs_per_contact_per_week:  z.number().int().min(1).max(10),
  max_msgs_per_contact_per_month: z.number().int().min(1).max(30),
  require_confirmation_above:     z.number().int().min(10).max(1000),
  optout_footer:                  z.string().min(10).max(300),
  send_window_start:              z.string().regex(/^\d{2}:\d{2}$/),
  send_window_end:                z.string().regex(/^\d{2}:\d{2}$/),
  allowed_weekdays:               z.array(z.number().int().min(0).max(6)).min(1),
});

type SettingsFormValues = z.infer<typeof settingsSchema>;

// ── Hooks de dados ─────────────────────────────────────────────────────────────

function useWhatsAppConnection() {
  return useQuery({
    queryKey: ['tenant-whatsapp'],
    queryFn: async () => {
      const { data, error } = await (supabase as any)
        .from('tenant_whatsapp')
        .select('provider, display_phone, status, tier, daily_limit, connected_at, last_health_check, health_status')
        .maybeSingle();
      if (error) throw error;
      return (data ?? null) as TenantWhatsApp | null;
    },
  });
}

function usePlanLimits() {
  return useQuery({
    queryKey: ['tenant-plan-limits'],
    queryFn: async () => {
      const { data, error } = await (supabase as any)
        .from('tenant_plan_limits')
        .select('plan_name, daily_message_limit, monthly_message_limit, max_campaign_size, max_campaigns_per_day, allowed_categories, min_cooldown_minutes, reputation_floor')
        .maybeSingle();
      if (error) throw error;
      return (data ?? null) as TenantPlanLimits | null;
    },
  });
}

function useMessagingSettings() {
  return useQuery({
    queryKey: ['tenant-messaging-settings'],
    queryFn: async () => {
      const { data, error } = await (supabase as any)
        .from('tenant_messaging_settings')
        .select('*')
        .maybeSingle();
      if (error) throw error;
      return (data ?? null) as TenantMessagingSettings | null;
    },
  });
}

// ── Componente principal ───────────────────────────────────────────────────────

export function WhatsAppSettings() {
  return (
    <div className="flex flex-col gap-6 max-w-2xl">
      <ConnectionPanel />
      <PlanLimitsPanel />
      <MessagingSettingsPanel />
      <OptOutFooterInfo />
    </div>
  );
}

// ── Painel 1: Conexão ─────────────────────────────────────────────────────────

function ConnectionPanel() {
  const { data: wa, isLoading } = useWhatsAppConnection();

  const statusConfig = {
    active:    { icon: CheckCircle2, color: 'text-emerald-400', label: 'Ativo',     bg: 'bg-emerald-400/10 border-emerald-400/20' },
    pending:   { icon: AlertCircle,  color: 'text-amber-400',   label: 'Pendente',  bg: 'bg-amber-400/10 border-amber-400/20'     },
    suspended: { icon: ShieldAlert,  color: 'text-rose-400',    label: 'Suspenso',  bg: 'bg-rose-400/10 border-rose-400/20'       },
    inactive:  { icon: XCircle,      color: 'text-ink-subtle',  label: 'Inativo',   bg: 'bg-white/5 border-hairline'              },
  };

  const tierLabels: Record<number, string> = {
    1: 'Tier 1 — até 1.000 msg/dia',
    2: 'Tier 2 — até 10.000 msg/dia',
    3: 'Tier 3 — até 100.000 msg/dia',
    4: 'Tier Enterprise — ilimitado',
  };

  return (
    <section className="glass border border-hairline rounded-2xl overflow-hidden">
      <div className="px-6 py-4 border-b border-hairline flex items-center gap-3">
        <div className="h-8 w-8 rounded-lg bg-[#25D366]/15 flex items-center justify-center">
          <MessageSquare className="h-4 w-4 text-[#25D366]" />
        </div>
        <div>
          <h2 className="font-semibold text-ink">Conexão WhatsApp</h2>
          <p className="text-xs text-ink-muted">YCloud Business API</p>
        </div>
      </div>

      <div className="p-6">
        {isLoading ? (
          <div className="flex items-center gap-2 text-ink-muted">
            <Loader2 className="h-4 w-4 animate-spin" />
            <span className="text-sm">Carregando...</span>
          </div>
        ) : !wa ? (
          <div className="flex flex-col gap-4">
            <div className="flex items-start gap-3 p-4 rounded-xl bg-amber-400/5 border border-amber-400/20">
              <AlertCircle className="h-5 w-5 text-amber-400 shrink-0 mt-0.5" />
              <div>
                <p className="text-sm font-medium text-ink">Integração não configurada</p>
                <p className="text-xs text-ink-muted mt-1">
                  Para ativar o WhatsApp, entre em contato com o suporte da plataforma.
                  Será necessário registrar um número no YCloud e vincular sua conta WhatsApp Business.
                </p>
              </div>
            </div>
            <StepsList />
          </div>
        ) : (
          <div className="flex flex-col gap-4">
            {/* Status badge */}
            {(() => {
              const cfg = statusConfig[wa.status] ?? statusConfig.inactive;
              const Icon = cfg.icon;
              return (
                <div className={`flex items-center gap-2 px-4 py-2 rounded-xl border w-fit ${cfg.bg}`}>
                  <Icon className={`h-4 w-4 ${cfg.color}`} />
                  <span className={`text-sm font-semibold ${cfg.color}`}>{cfg.label}</span>
                </div>
              );
            })()}

            {/* Dados do número */}
            <div className="grid grid-cols-2 gap-4">
              <InfoRow label="Número" value={wa.display_phone} />
              <InfoRow label="Provedor" value={wa.provider.toUpperCase()} />
              <InfoRow label="Capacidade" value={tierLabels[wa.tier] ?? `Tier ${wa.tier}`} />
              <InfoRow
                label="Limite diário"
                value={wa.daily_limit.toLocaleString('pt-BR') + ' mensagens'}
              />
              {wa.connected_at && (
                <InfoRow
                  label="Conectado em"
                  value={new Date(wa.connected_at).toLocaleDateString('pt-BR')}
                />
              )}
              {wa.last_health_check && (
                <InfoRow
                  label="Último health check"
                  value={new Date(wa.last_health_check).toLocaleString('pt-BR')}
                />
              )}
            </div>

            {wa.health_status && wa.health_status !== 'ok' && (
              <div className="flex items-start gap-3 p-3 rounded-xl bg-rose-400/5 border border-rose-400/20">
                <AlertCircle className="h-4 w-4 text-rose-400 shrink-0 mt-0.5" />
                <p className="text-xs text-rose-300">{wa.health_status}</p>
              </div>
            )}
          </div>
        )}
      </div>
    </section>
  );
}

function StepsList() {
  return (
    <div className="flex flex-col gap-2">
      <p className="text-xs font-semibold text-ink-muted uppercase tracking-wider">Passos para ativar</p>
      {[
        'Criar conta no YCloud e registrar seu número WhatsApp Business',
        'Enviar o número e a chave de API ao suporte da plataforma',
        'Aguardar a ativação (geralmente até 1 dia útil)',
        'Submeter templates de mensagem para aprovação da Meta',
      ].map((step, i) => (
        <div key={i} className="flex items-start gap-3">
          <span className="h-5 w-5 rounded-full bg-white/5 border border-hairline text-[10px] font-bold flex items-center justify-center text-ink-muted shrink-0 mt-0.5">
            {i + 1}
          </span>
          <p className="text-sm text-ink-muted">{step}</p>
        </div>
      ))}
    </div>
  );
}

function InfoRow({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <p className="text-xs text-ink-muted mb-0.5">{label}</p>
      <p className="text-sm font-semibold text-ink">{value}</p>
    </div>
  );
}

// ── Painel 2: Limites do plano (read-only) ────────────────────────────────────

function PlanLimitsPanel() {
  const { data: limits, isLoading } = usePlanLimits();

  const planColors: Record<string, string> = {
    starter:    'bg-zinc-400/10 text-zinc-300 border-zinc-400/20',
    growth:     'bg-cyan-400/10 text-cyan-300 border-cyan-400/20',
    pro:        'bg-violet-400/10 text-violet-300 border-violet-400/20',
    enterprise: 'bg-amber-400/10 text-amber-300 border-amber-400/20',
  };

  return (
    <section className="glass border border-hairline rounded-2xl overflow-hidden">
      <div className="px-6 py-4 border-b border-hairline flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="h-8 w-8 rounded-lg bg-accent/10 flex items-center justify-center">
            <ShieldAlert className="h-4 w-4 text-accent" />
          </div>
          <div>
            <h2 className="font-semibold text-ink">Limites do plano</h2>
            <p className="text-xs text-ink-muted">Definidos pelo administrador da plataforma</p>
          </div>
        </div>
        {limits && (
          <span className={`text-xs font-semibold px-2 py-1 rounded-lg border ${planColors[limits.plan_name] ?? planColors.starter}`}>
            {limits.plan_name.toUpperCase()}
          </span>
        )}
      </div>

      <div className="p-6">
        {isLoading ? (
          <Loader2 className="h-4 w-4 animate-spin text-ink-muted" />
        ) : !limits ? (
          <p className="text-sm text-ink-muted">Plano não configurado.</p>
        ) : (
          <div className="grid grid-cols-2 gap-x-8 gap-y-4">
            <LimitRow label="Mensagens/dia" value={limits.daily_message_limit.toLocaleString('pt-BR')} />
            <LimitRow label="Mensagens/mês" value={limits.monthly_message_limit.toLocaleString('pt-BR')} />
            <LimitRow label="Contatos por campanha" value={limits.max_campaign_size.toLocaleString('pt-BR')} />
            <LimitRow label="Campanhas por dia" value={String(limits.max_campaigns_per_day)} />
            <LimitRow
              label="Cooldown mínimo"
              value={limits.min_cooldown_minutes < 60
                ? `${limits.min_cooldown_minutes} min`
                : `${Math.round(limits.min_cooldown_minutes / 60)} h`}
            />
            <LimitRow
              label="Reputação mínima Meta"
              value={`${(limits.reputation_floor * 100).toFixed(1)}%`}
            />
            <div className="col-span-2">
              <p className="text-xs text-ink-muted mb-1">Categorias permitidas</p>
              <div className="flex flex-wrap gap-2">
                {limits.allowed_categories.map((cat) => (
                  <span
                    key={cat}
                    className="text-xs px-2.5 py-1 rounded-full bg-accent/10 text-accent border border-accent/20 font-medium"
                  >
                    {cat}
                  </span>
                ))}
              </div>
            </div>
          </div>
        )}
      </div>
    </section>
  );
}

function LimitRow({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <p className="text-xs text-ink-muted">{label}</p>
      <p className="text-sm font-bold text-ink mt-0.5">{value}</p>
    </div>
  );
}

// ── Painel 3: Configurações de envio (editável) ───────────────────────────────

const WEEKDAY_LABELS = ['Dom', 'Seg', 'Ter', 'Qua', 'Qui', 'Sex', 'Sáb'];

function MessagingSettingsPanel() {
  const qc = useQueryClient();
  const { data: settings, isLoading } = useMessagingSettings();

  const {
    register,
    handleSubmit,
    setValue,
    watch,
    reset,
    formState: { errors, isDirty, isSubmitting },
  } = useForm<SettingsFormValues>({
    resolver: zodResolver(settingsSchema),
    defaultValues: {
      max_msgs_per_contact_per_week:  2,
      max_msgs_per_contact_per_month: 6,
      require_confirmation_above:     100,
      optout_footer:                  'Para não receber mais mensagens, responda SAIR.',
      send_window_start:              '09:00',
      send_window_end:                '20:00',
      allowed_weekdays:               [1, 2, 3, 4, 5, 6],
    },
  });

  useEffect(() => {
    if (settings) {
      reset({
        max_msgs_per_contact_per_week:  settings.max_msgs_per_contact_per_week,
        max_msgs_per_contact_per_month: settings.max_msgs_per_contact_per_month,
        require_confirmation_above:     settings.require_confirmation_above,
        optout_footer:                  settings.optout_footer,
        send_window_start:              settings.send_window_start,
        send_window_end:                settings.send_window_end,
        allowed_weekdays:               settings.allowed_weekdays,
      });
    }
  }, [settings, reset]);

  const allowedWeekdays = watch('allowed_weekdays');

  const saveMutation = useMutation({
    mutationFn: async (values: SettingsFormValues) => {
      const { error } = await (supabase as any)
        .from('tenant_messaging_settings')
        .upsert({ ...values }, { onConflict: 'tenant_id' });
      if (error) throw error;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['tenant-messaging-settings'] });
    },
  });

  const toggleWeekday = (day: number) => {
    const current = allowedWeekdays ?? [];
    if (current.includes(day)) {
      if (current.length === 1) return; // mínimo 1 dia
      setValue('allowed_weekdays', current.filter((d) => d !== day), { shouldDirty: true });
    } else {
      setValue('allowed_weekdays', [...current, day].sort(), { shouldDirty: true });
    }
  };

  return (
    <section className="glass border border-hairline rounded-2xl overflow-hidden">
      <div className="px-6 py-4 border-b border-hairline flex items-center gap-3">
        <div className="h-8 w-8 rounded-lg bg-violet-400/10 flex items-center justify-center">
          <Clock className="h-4 w-4 text-violet-400" />
        </div>
        <div>
          <h2 className="font-semibold text-ink">Configurações de envio</h2>
          <p className="text-xs text-ink-muted">Editáveis dentro dos limites do seu plano</p>
        </div>
      </div>

      <form
        onSubmit={handleSubmit((values) => saveMutation.mutateAsync(values))}
        className="p-6 flex flex-col gap-6"
      >
        {isLoading ? (
          <Loader2 className="h-4 w-4 animate-spin text-ink-muted" />
        ) : (
          <>
            {/* Frequência por contato */}
            <fieldset>
              <legend className="flex items-center gap-2 text-sm font-semibold text-ink mb-3">
                <Users className="h-4 w-4 text-ink-muted" />
                Frequência por contato
              </legend>
              <div className="grid grid-cols-2 gap-4">
                <FormField
                  label="Máx. msgs por semana"
                  error={errors.max_msgs_per_contact_per_week?.message}
                >
                  <input
                    type="number"
                    min={1}
                    max={10}
                    className="input-base w-full"
                    {...register('max_msgs_per_contact_per_week', { valueAsNumber: true })}
                  />
                </FormField>
                <FormField
                  label="Máx. msgs por mês"
                  error={errors.max_msgs_per_contact_per_month?.message}
                >
                  <input
                    type="number"
                    min={1}
                    max={30}
                    className="input-base w-full"
                    {...register('max_msgs_per_contact_per_month', { valueAsNumber: true })}
                  />
                </FormField>
              </div>
            </fieldset>

            {/* Confirmação dupla */}
            <FormField
              label="Confirmação dupla para campanhas acima de"
              hint="Número mínimo de contatos que exige segunda confirmação antes do disparo"
              error={errors.require_confirmation_above?.message}
            >
              <div className="flex items-center gap-2">
                <input
                  type="number"
                  min={10}
                  max={1000}
                  className="input-base w-28"
                  {...register('require_confirmation_above', { valueAsNumber: true })}
                />
                <span className="text-sm text-ink-muted">contatos</span>
              </div>
            </FormField>

            {/* Janela de envio */}
            <fieldset>
              <legend className="flex items-center gap-2 text-sm font-semibold text-ink mb-3">
                <Clock className="h-4 w-4 text-ink-muted" />
                Janela de envio
              </legend>
              <div className="grid grid-cols-2 gap-4 mb-4">
                <FormField label="Início" error={errors.send_window_start?.message}>
                  <input type="time" className="input-base w-full" {...register('send_window_start')} />
                </FormField>
                <FormField label="Fim" error={errors.send_window_end?.message}>
                  <input type="time" className="input-base w-full" {...register('send_window_end')} />
                </FormField>
              </div>

              {/* Dias da semana */}
              <div>
                <p className="text-xs text-ink-muted mb-2">Dias permitidos</p>
                <div className="flex gap-1.5 flex-wrap">
                  {WEEKDAY_LABELS.map((label, day) => {
                    const active = (allowedWeekdays ?? []).includes(day);
                    return (
                      <button
                        key={day}
                        type="button"
                        onClick={() => toggleWeekday(day)}
                        className={`h-9 w-9 rounded-lg text-xs font-semibold transition-all border ${
                          active
                            ? 'bg-accent/20 border-accent/40 text-accent'
                            : 'bg-white/5 border-hairline text-ink-muted hover:bg-white/10'
                        }`}
                      >
                        {label}
                      </button>
                    );
                  })}
                </div>
                {errors.allowed_weekdays && (
                  <p className="text-xs text-rose-400 mt-1">{errors.allowed_weekdays.message}</p>
                )}
              </div>
            </fieldset>

            {/* Rodapé LGPD */}
            <FormField
              label="Rodapé de opt-out (LGPD)"
              hint="Acrescentado automaticamente ao final de cada mensagem enviada"
              error={errors.optout_footer?.message}
            >
              <textarea
                rows={2}
                className="input-base w-full resize-none"
                {...register('optout_footer')}
              />
            </FormField>

            {/* Feedback de erros */}
            {saveMutation.isError && (
              <div className="flex items-center gap-2 text-rose-400 text-sm">
                <XCircle className="h-4 w-4 shrink-0" />
                <span>Erro ao salvar configurações. Tente novamente.</span>
              </div>
            )}
            {saveMutation.isSuccess && !isDirty && (
              <div className="flex items-center gap-2 text-emerald-400 text-sm">
                <CheckCircle2 className="h-4 w-4 shrink-0" />
                <span>Configurações salvas com sucesso.</span>
              </div>
            )}

            <div className="flex justify-end pt-2">
              <button
                type="submit"
                disabled={!isDirty || isSubmitting || saveMutation.isPending}
                className="flex items-center gap-2 px-5 py-2.5 rounded-xl bg-accent text-white text-sm font-semibold transition-all hover:bg-accent/80 disabled:opacity-50 disabled:cursor-not-allowed min-w-[120px] justify-center"
              >
                {saveMutation.isPending ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  'Salvar'
                )}
              </button>
            </div>
          </>
        )}
      </form>
    </section>
  );
}

// ── Informativo LGPD ──────────────────────────────────────────────────────────

function OptOutFooterInfo() {
  return (
    <div className="flex items-start gap-3 p-4 rounded-xl bg-white/3 border border-hairline">
      <Wifi className="h-4 w-4 text-ink-muted shrink-0 mt-0.5" />
      <div>
        <p className="text-xs font-semibold text-ink-muted uppercase tracking-wider mb-1">
          Conformidade LGPD
        </p>
        <p className="text-xs text-ink-muted leading-relaxed">
          O rodapé de opt-out é injetado automaticamente pela plataforma em cada mensagem enviada.
          Respostas com as palavras <strong className="text-ink">SAIR</strong>, <strong className="text-ink">STOP</strong>{' '}
          ou <strong className="text-ink">PARAR</strong> são detectadas pelo webhook e registradas no
          banco de dados. O contato não receberá mais mensagens desta campanha.
          Base legal: LGPD Art. 18 I — revogação do consentimento.
        </p>
      </div>
    </div>
  );
}

// ── Utilitários de formulário ─────────────────────────────────────────────────

function FormField({
  label,
  hint,
  error,
  children,
}: {
  label: string;
  hint?: string;
  error?: string;
  children: React.ReactNode;
}) {
  return (
    <div className="flex flex-col gap-1.5">
      <label className="text-xs font-semibold text-ink">{label}</label>
      {hint && <p className="text-xs text-ink-muted -mt-1">{hint}</p>}
      {children}
      {error && <p className="text-xs text-rose-400">{error}</p>}
    </div>
  );
}
